import { ICustomerAddress } from "@/api_framework/api_modals/Addresses";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ROUTES } from "@/app/routes";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { BookmarkIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useState } from "react";
import { IAddressBlockType2Props } from "@components/routes/auth_routes/CustomerProfile/customer_profile_interface";
import AddressBlock from "../AddressBlock";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";

const AddressBlockType2: React.FC<IAddressBlockType2Props> = (props) => {
  const { setVisible, setModalData } = useModalActions("ADD_ADDRESS_MODAL");
  const { NO_ADDRESS } = useImageConstants();

  const { address_labels: addressLabels } = useArrayConstants();
  const [selectedLabel, setSelectedLabel] = useState<string>(addressLabels[0]?.label ?? "1");
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { customerAddresses, customerId, customerDetails } = routeState.state as ICustomerProfileState;

  const labelName = (labelId: string) => {
    return addressLabels?.filter((al) => al?.label === labelId)[0]?.value;
  };

  const reducedAddresses = customerAddresses?.reduce(
    (final, address: ICustomerAddress) => {
      if (address?.default_address === true || address?.label === "1") {
        final["1"].push(address);
      }
      if (address.label === "2") {
        final["2"].push(address);
      }
      if (address?.label === "3") {
        final["3"].push(address);
      }
      return final;
    },
    {
      "1": [],
      "2": [],
      "3": [],
    },
  );

  return (
    <div className="w-full">
      <div className="flex justify-between items-center gap-3">
        <span className="text-sm font-semibold text-gray-700">Addresses</span>
        <button
          onClick={() => {
            setModalData({
              userId: customerId,
              customerName: customerDetails?.name,
            });
            setVisible();
          }}
          type="button"
          className="py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 flex items-center gap-2"
        >
          + Add Address
        </button>
      </div>
      <div className="mt-4 items-start border shadow-[0px_4px_4px_rgba(0,0,0,0.05),0px_-2px_4px_rgba(0,0,0,0.05)] bg-white opacity-100 p-3 rounded-[10px] border-[rgba(128,141,160,0.2)] grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        <div className="md:col-span-3 h-full">
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-1 gap-2">
            {addressLabels.map((adl, idx) => {
              return (
                <span
                  key={`${idx}:${adl?.value}:${adl?.label}`}
                  className="isolate inline-flex rounded-md w-full shadow-sm"
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedLabel(adl?.label);
                    }}
                    type="button"
                    className="relative w-full inline-flex items-center gap-x-1.5 rounded-l-md bg-white px-3 py-2 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                  >
                    <BookmarkIcon
                      className={clsx(
                        "-ml-0.5 h-5 w-5 ",
                        selectedLabel === adl?.label ? "text-blue-500" : "text-gray-600",
                      )}
                      aria-hidden="true"
                    />
                    {adl?.value}
                  </button>
                  <button
                    type="button"
                    className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                  >
                    {reducedAddresses[parseInt(adl?.label)].length}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-9 md:border-l md:border-l-gray-200 md:pl-4 max-h-56 overflow-y-auto">
          {reducedAddresses[selectedLabel]?.length > 0 ? (
            <div className="mt-2">
              <AddressBlock
                label={selectedLabel as any}
                addressFieldActions={props?.addressFieldActions}
                formHook={props?.formHook}
                isFormUpdate={props?.isFormUpdate}
                disabled={props?.disabled}
              />
            </div>
          ) : (
            <div className="w-full h-full flex justify-center items-center text-center text-xs text-gray-400 ">
              <NoDataFoundComponent
                message={`No ${labelName(selectedLabel)} Addresses`}
                className="w-20 h-20"
                description="No address found under selected label, please add a few."
                imageURL={NO_ADDRESS}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressBlockType2;
